// A run's stops as one line, like a transit map (docs/spec/transport.md slice 7): done stops ticked
// and folded, the next one marigold with a button for each child, the rest with their children's
// names. The same line sits on the run page and beside the map on the tablet's drive screen.
import { Badge, Button, Icon } from "@vavr/ui";
import { fullName } from "@vavr/screens/shared";
import { actionAt, type Action, type DriveRider, type DriveRoute, type DriveStop } from "./drive.ts";
import type { RunMapStop } from "./runmap.tsx";
import { clock, Status, type Tone } from "./status.tsx";

/** `meet_by` "parent": the parent chose who meets them at the stop (with `meet_reason`); the driver sees it. */
export interface LineRider extends DriveRider { site_id: string; first: string; last: string; flags?: string[]; meet_by?: string; meet_reason?: string | null; not_riding_late?: boolean; not_riding_why?: string | null }
export type LineStop = DriveStop & { lat: number | null; lng: number | null };
type State = "done" | "next" | "todo";

const LABEL: Record<DriveRider["status"], { label: string; tone: Tone }> = {
  waiting: { label: "Not yet", tone: "neutral" }, on: { label: "On the bus", tone: "info" }, off: { label: "Off", tone: "success" },
  absent: { label: "Not at the stop", tone: "warning" }, held: { label: "Kept on the bus", tone: "danger" },
};

/** The stops in order, and where the run is: stops before the next one are done. */
export function lineStates<S extends DriveStop>(stops: S[], next: DriveStop | null, started: boolean, finished: boolean) {
  const ordered = [...stops].sort((a, b) => a.seq - b.seq);
  const nextAt = next ? ordered.findIndex((s) => s.id === next.id) : finished ? ordered.length : started ? ordered.length : -1;
  const home = ordered.filter((s) => !s.site_id);
  return ordered.map((stop, i) => ({ stop, state: (i < nextAt ? "done" : i === nextAt ? "next" : "todo") as State, n: stop.site_id ? null : home.indexOf(stop) + 1 }));
}

/** The same stops for a map: numbered, done greyed, the next one marigold. */
export const mapStops = (line: ReturnType<typeof lineStates<LineStop>>): RunMapStop[] =>
  line.map(({ stop, state, n }) => ({ key: stop.id, name: stop.name, lat: stop.lat, lng: stop.lng, school: !!stop.site_id, n: n ?? undefined, state }));

/** Who is dealt with at a stop: at school, those getting on (afternoon) or off (morning); elsewhere, that stop's riders. */
function whoAt<R extends LineRider>(route: DriveRoute, stop: DriveStop, riders: R[], state: State) {
  if (!stop.site_id) return riders.filter((r) => r.stop_id === stop.id);
  if (route.kind === "am") return riders.filter((r) => r.status === "on" || r.status === "held" || (state === "done" && r.status === "off"));
  if (route.kind === "pm") return riders.filter((r) => (state === "done" ? r.status !== "waiting" : r.status === "waiting" || r.status === "held"));
  return riders;
}

export function StopLine<R extends LineRider>({ route, stops, riders, next, started, finished, onAct, onHandTo }: {
  route: DriveRoute; stops: LineStop[]; riders: R[]; next: DriveStop | null; started: boolean; finished: boolean;
  onAct: (x: R, action: Action | "absent", stop: DriveStop) => void; onHandTo: (x: R) => void;
}) {
  const line = lineStates(stops, next, started, finished);
  return (
    <ol className="pmt-line">
      {line.map(({ stop, state, n }) => {
        const here = whoAt(route, stop, riders, state);
        const live = started && !finished;
        const rows = (
          <ul className="pmt-line__kids">
            {here.map((x) => {
              const act = live ? actionAt(route, stop, x) : null;
              const label = x.not_riding && x.status === "waiting" ? { label: x.not_riding_why === "absent" ? "Absent today" : x.not_riding_late ? "Not riding today (late notice)" : "Not riding today", tone: "warning" as Tone }
                : route.kind === "pm" && !stop.site_id && x.status === "waiting" ? { label: "Gets on at school", tone: "neutral" as Tone } : LABEL[x.status];
              return (
                <li key={x.child_id} className="pmt-line__kid">
                  <div className="pmt-line__who">
                    <span className="pmt-line__name">{fullName(x)}</span>
                    <Status tone={label.tone}>{label.label}</Status>
                    {x.flags?.includes("unauthorised_adult") && <Badge tone="danger">Not an approved adult</Badge>}
                    {x.flags?.includes("restriction_hit") && <Badge tone="danger">Restriction</Badge>}
                  </div>
                  {!stop.site_id && (x.meet_by === "parent" || x.meet_by === "office") && (
                    <p className="pmt-line__note">{x.meet_required ? "An adult meets them" : "May walk home from the stop"} · {x.meet_by === "parent" ? "parent's choice" : "office's note"}{x.meet_reason ? `: “${x.meet_reason}”` : ""}</p>
                  )}
                  {act && <KidActions act={act} onAct={(a) => onAct(x, a, stop)} onHandTo={() => onHandTo(x)} />}
                </li>
              );
            })}
          </ul>
        );
        return (
          <li key={stop.id} className="pmt-line__stop" data-state={state} aria-label={`${stop.name}${state === "next" ? ", next" : state === "done" ? ", done" : ""}`}>
            <span className={`pmt-line__node${stop.site_id ? " pmt-line__node--school" : ""}`} aria-hidden="true">
              {state === "done" ? <Icon name="check" /> : stop.site_id ? <Icon name="home" /> : n}
            </span>
            <div className="pmt-line__body">
              <div className="pmt-line__head">
                <h3 className="pmt-line__stopname">{stop.name}</h3>
                {state === "next" && <span className="pmt-line__tag">Next</span>}
                <span className="pmt-line__time">{clock(stop.time) ?? ""}</span>
              </div>
              {here.length === 0 ? <p className="pmt-line__none">{stop.site_id ? "School" : "Nobody for this stop"}</p>
                : state === "next" ? rows
                : state === "done" ? <details className="pmt-line__more"><summary>{here.length} {here.length === 1 ? "child" : "children"} · done</summary>{rows}</details>
                : (
                  <>
                    <p className="pmt-line__chips">{here.map((x) => <span key={x.child_id} className="pmt-line__chip">{x.first}</span>)}</p>
                    {live && here.some((x) => actionAt(route, stop, x)) && <details className="pmt-line__more"><summary>Deal with this stop now</summary>{rows}</details>}
                  </>
                )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** One child's buttons: one tap each. */
function KidActions({ act, onAct, onHandTo }: { act: Action; onAct: (a: Action | "absent") => void; onHandTo: () => void }) {
  return (
    <div className="pmt-line__acts">
      {act === "board" && <><Button onClick={() => onAct("board")}>On the bus</Button><Button variant="secondary" onClick={() => onAct("absent")}>Not here</Button></>}
      {act === "alight" && <Button onClick={() => onAct("alight")}>Off the bus</Button>}
      {act === "alight-school" && <Button onClick={() => onAct("alight-school")}>Off at school</Button>}
      {act === "handover" && <Button onClick={onHandTo}>Hand to an adult</Button>}
    </div>
  );
}
